import { Currency, TimeZone } from "@/interfaces/geographical";
import { Timezone } from "@/interfaces/timezone";
import { i18n } from "@/plugins/i18n";
import sortBy from "lodash/sortBy";
import HttpService from "./http.service";
import { messagesService } from "./messages.service";

export interface Country {
  id: string;
  name: string;
  fips_code?: string;
}

export interface State {
  id: number;
  name: string;
  country_id: string;
  code: string;
}

class GeographicalService extends HttpService {
  public async getCountries(searchString: string = ""): Promise<Country[]> {
    this.uri = `/countries`;

    this.query = {
      no_pagination: "",
      sort: "name"
    };
    if (searchString) {
      this.query[`q[name_contains]`] = searchString;
    }
    const response = await super.get({ ...this.query });

    let countries: Country[] = response.data.map((country: Country) => {
      return {
        id: country.fips_code,
        name: i18n.locale === "en" ? country.name : country.name
      };
    });
    const USA = {
      id: "US",
      name: "United States"
    };
    countries = countries.filter(country => country !== USA);
    countries.unshift(USA);
    return countries;
  }

  public async getStates(countryId: string | undefined): Promise<object[]> {
    this.uri = `/states`;
    this.query = {
      no_pagination: true,
      sort: "name"
    };
    if (countryId) {
      this.query.country_id = countryId;
    }
    const response = await super.get(this.query);
    return response.data;
  }

  public async getCities(stateId: number | undefined): Promise<object[]> {
    this.uri = `/cities`;
    this.query = {};

    if (stateId) {
      this.query.state_id = stateId;
    }
    const response = await super.get(this.query);
    return response.data.data;
  }
  public async getTimezones(): Promise<object[]> {
    this.uri = `${process.env.VUE_APP_BASE_URL}/timezones`;
    this.query = {
      no_pagination: ""
    };
    const response = await super.get(this.query);

    const timezones = response.data.map((timezone: TimeZone) => {
      return {
        id: timezone.id,
        name: timezone.zone
      };
    });
    return sortBy(timezones, ["name"]);
  }

  /**
   * Gets timezone by id
   * @param id: number
   * @returns timezone by id
   */
  public async getTimezoneByID(id: number): Promise<Timezone | void> {
    try {
      this.uri = `${process.env.VUE_APP_BASE_URL}/timezones/${id}`;
      const response = await super.get(this.query);
      return response.data;
    } catch (e) {
      messagesService.renderErrorMessage(e);
    }
  }

  public async getCurrencies(): Promise<Currency[]> {
    this.uri = `${process.env.VUE_APP_BASE_URL}/currencies`;
    this.query = {
      no_pagination: ""
    };
    const response = await super.get(this.query);
    return response.data.map((currency: Currency) => {
      return {
        id: currency.id,
        code: currency.code
      };
    });
  }
}

export const geographicalService = new GeographicalService();
